import React from 'react';
import { Typography, Button } from 'antd';
import { Condition, NestedConditionGroup } from '../types';
import { mapOperatorFromApi, mapKeyFromApi } from '../utils';

const { Text } = Typography;

interface ConditionTableProps {
  conditions: (Condition | NestedConditionGroup)[];
  operatorType: 'or' | 'and';
  title: string;
  titleStyle?: React.CSSProperties;
  level?: number;
}

const ConditionTable: React.FC<ConditionTableProps> = ({
  conditions,
  operatorType,
  title,
  titleStyle = {},
  level = 0,
}) => {
  // Separate flat conditions from nested groups
  const flatConditions = conditions.filter((condition): condition is Condition => {
    if ('cond' in condition) return false; // It's a nested group

    // Filter by type for URL conditions
    if (title === 'URL') {
      return ['path', 'host', 'sch'].includes(condition.key);
    }
    return true; // For Response conditions
  });

  const nestedGroups = conditions.filter((condition): condition is NestedConditionGroup => {
    return 'cond' in condition && Array.isArray(condition.cond);
  });

  if (flatConditions.length === 0 && nestedGroups.length === 0) return null;

  return (
    <div style={{ display: 'flex', alignItems: 'start', gap: 20 }}>
      <div style={{ paddingTop: '20px', minWidth: '80px' }}>
        <span
          style={{
            fontSize: '14px',
            fontWeight: '300',
            color: 'rgba(31, 31, 31, 1)',
            ...titleStyle,
          }}
        >
          {title}:{' '}
        </span>
      </div>
      <div style={{ padding: '12px', marginBottom: 8, marginLeft: level * 24 }}>
        {/* Render flat conditions */}
        {flatConditions
          .filter((condition) => condition && condition.key) // Filter out invalid conditions
          .map((condition, index, validConditions) => {
            const key = title === 'URL' ? condition.key : mapKeyFromApi(condition.key || '');
            const operator = mapOperatorFromApi(condition.op || condition.operation || '');
            const value = Array.isArray(condition.val)
              ? condition.val.join(', ')
              : String(condition.val || '');

            const displayKey =
              title === 'URL'
                ? condition.key && condition.key.length > 0
                  ? condition.key.charAt(0).toUpperCase() + condition.key.slice(1)
                  : 'Unknown'
                : key === 'duration'
                ? 'Duration (ms)'
                : key === 'responseCode'
                ? 'Response Code'
                : key && key.length > 0
                ? key.charAt(0).toUpperCase() + key.slice(1)
                : 'Unknown';

            return (
              <div
                key={index}
                style={{
                  marginBottom: 8,
                  display: 'flex',
                  alignItems: 'center',
                  padding: '8px 0',
                }}
              >
                {/* Column 1: Condition Type */}
                <div style={{ minWidth: title === 'URL' ? '80px' : '120px', textAlign: 'left' }}>
                  <Text>{displayKey}</Text>
                </div>

                {/* Grey separator */}
                <div
                  style={{
                    width: '1px',
                    height: '20px',
                    backgroundColor: '#d9d9d9',
                    margin: '0 16px',
                  }}
                />

                {/* Column 2: Operator */}
                <div style={{ minWidth: '120px', textAlign: 'left' }}>
                  <Text style={{ fontStyle: 'italic' }}>{operator}</Text>
                </div>

                {/* Grey separator */}
                <div
                  style={{
                    width: '1px',
                    height: '20px',
                    backgroundColor: '#d9d9d9',
                    margin: '0 16px',
                  }}
                />

                {/* Column 3: Values */}
                <div style={{ minWidth: '200px', textAlign: 'left', flex: 1 }}>
                  <Text>{value}</Text>
                </div>

                {/* OR/AND operator at the end */}
                {(index < validConditions.length - 1 || nestedGroups.length > 0) && (
                  <div style={{ marginLeft: '16px' }}>
                    <Button
                      type="text"
                      size="small"
                      style={{
                        background: '#f0f0f0',
                        border: '1px solid #d9d9d9',
                        borderRadius: 16,
                        padding: '2px 8px',
                        height: 24,
                        fontSize: 12,
                      }}
                    >
                      {operatorType.toUpperCase()}
                    </Button>
                  </div>
                )}
              </div>
            );
          })}

        {/* Render nested groups */}
        {nestedGroups.map((group, groupIndex) => (
          <React.Fragment key={`group-${groupIndex}`}>
            <div
              style={{
                border: '1px solid #e8e8e8',
                borderRadius: 8,
                padding: 16,
                marginTop: 16,
                backgroundColor: 'rgba(0, 0, 0, 0.02)',
              }}
            >
              <div style={{ marginBottom: 12 }}>
                <Text strong style={{ fontSize: '12px', color: '#666' }}>
                  Group ({group.type.toUpperCase()} Logic):
                </Text>
              </div>
              <ConditionTable
                conditions={group.cond}
                operatorType={group.type}
                title={title}
                level={level + 1}
                titleStyle={{ display: 'none' }} // Hide title for nested groups
              />
            </div>

            {/* OR/AND operator between groups */}
            {groupIndex < nestedGroups.length - 1 && (
              <div style={{ display: 'flex', justifyContent: 'center', margin: '12px 0' }}>
                <Button
                  type="text"
                  size="small"
                  style={{
                    background: '#f0f0f0',
                    border: '1px solid #d9d9d9',
                    borderRadius: 16,
                    padding: '2px 8px',
                    height: 24,
                    fontSize: 12,
                  }}
                >
                  {operatorType.toUpperCase()}
                </Button>
              </div>
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

export default ConditionTable;
